import { useMutation } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  requestPasswordResetSchema,
  resetPasswordSchema,
} from '@wingseat/shared';
import { useAuth } from '../auth/auth-context';
import { ApiError, api } from '../lib/api-client';

export function PasswordResetPage() {
  const [params] = useSearchParams();
  const tokenFromUrl = params.get('token');
  const navigate = useNavigate();
  const { refetch } = useAuth();
  const [mode, setMode] = useState<'request' | 'set'>('request');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErr, setFieldErr] = useState<string | null>(null);

  useEffect(() => {
    if (tokenFromUrl) setMode('set');
  }, [tokenFromUrl]);

  const requestMut = useMutation({
    mutationFn: async () => {
      const parsed = requestPasswordResetSchema.safeParse({ email });
      if (!parsed.success) {
        setFieldErr(parsed.error.issues[0]?.message ?? 'Ungültig');
        throw new Error('validation');
      }
      setFieldErr(null);
      await api.post('/auth/request-password-reset', parsed.data);
    },
  });

  const setMut = useMutation({
    mutationFn: async () => {
      const parsed = resetPasswordSchema.safeParse({
        token: tokenFromUrl ?? '',
        password,
      });
      if (!parsed.success) {
        setFieldErr(parsed.error.issues[0]?.message ?? 'Ungültig');
        throw new Error('validation');
      }
      setFieldErr(null);
      // Server setzt bei Erfolg direkt eine Session (Auto-Login).
      await api.post('/auth/reset-password', parsed.data);
      await refetch();
      navigate('/app/flights', { replace: true });
    },
  });

  return (
    <div className="flex min-h-full items-center justify-center px-6 py-12">
      <div className="w-full max-w-sm rounded-2xl border border-slate-800 bg-slate-900/60 p-6 shadow-xl space-y-5">
        <header className="text-center">
          <h1 className="text-2xl font-semibold">Passwort zurücksetzen</h1>
        </header>

        {mode === 'request' && !requestMut.isSuccess && (
          <form
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              requestMut.mutate();
            }}
            className="space-y-4"
          >
            <label className="block text-sm font-medium">E-Mail</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-base"
            />
            {fieldErr && <p className="text-xs text-rose-400">{fieldErr}</p>}
            <button
              type="submit"
              disabled={requestMut.isPending}
              className="w-full rounded-lg bg-brand-600 px-4 py-2.5 text-base font-medium text-white hover:bg-brand-700 disabled:opacity-60"
            >
              {requestMut.isPending ? 'Sende …' : 'Reset-Link anfordern'}
            </button>
          </form>
        )}

        {mode === 'request' && requestMut.isSuccess && (
          <p className="text-sm text-emerald-300">
            Falls die E-Mail-Adresse bekannt ist, erhältst Du in Kürze eine
            Mail mit einem Reset-Link.
          </p>
        )}

        {mode === 'set' && !setMut.isSuccess && (
          <form
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              setMut.mutate();
            }}
            className="space-y-4"
          >
            <p className="text-xs text-slate-400">
              Gib Dein neues Passwort ein.
            </p>
            <label className="block text-sm font-medium">Neues Passwort</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-base"
            />
            {fieldErr && <p className="text-xs text-rose-400">{fieldErr}</p>}
            {setMut.error instanceof ApiError && (
              <p className="text-xs text-rose-400">{setMut.error.message}</p>
            )}
            <button
              type="submit"
              disabled={setMut.isPending}
              className="w-full rounded-lg bg-brand-600 px-4 py-2.5 text-base font-medium text-white hover:bg-brand-700 disabled:opacity-60"
            >
              {setMut.isPending ? 'Speichere …' : 'Passwort setzen'}
            </button>
          </form>
        )}

        {mode === 'set' && setMut.isSuccess && (
          <p className="text-sm text-emerald-300">
            Passwort gespeichert. Du wirst weitergeleitet …
          </p>
        )}

        <div className="text-center">
          <Link to="/login" className="text-xs text-brand-400 hover:underline">
            Zurück zum Login
          </Link>
        </div>
      </div>
    </div>
  );
}
